import getTarget from 'javascripts/utils/get-target';
import h from 'hyperscript';

// Tab bar
export default class TabBar {
  constructor($tabBar, resizeObserver) {
    // Get references
    this.$tabBar = $tabBar;
    this.$itemList = this.$tabBar.querySelector('.tab-bar__list');
    this.$items = this.$itemList?.querySelectorAll('.tab-bar__item');
    this.$select = this.$tabBar?.querySelector('.tab-bar__select');

    this.isTablist = this.$itemList?.getAttribute('role') === 'tablist';
    this.isSingleItem = this.$items?.length <= 1;
    this.isGlossary = this.$tabBar.classList.contains('tab-bar--glossary');

    if (this.isSingleItem || !this.$itemList || !this.$items) {
      return;
    }

    this.$activeItem = this.$tabBar.querySelector('.tab-bar__item--active');
    if (!this.$activeItem) {
      for (let i = 0; i < this.$items.length; i += 1) {
        if (this.$items[i].href) {
          this.$items[i].classList.add('tab-bar__item--active');
          this.$activeItem = this.$items[i];
          break;
        }
      }
    }
    // Register with observer
    resizeObserver.observe(this.$tabBar);

    // Outside click
    this.onOutsideClickBinded = this.onOutsideClick.bind(this);

    // Register events
    if (!this.isGlossary && !this.isSingleItem) {
      this.$select.addEventListener('click', this.onSelect.bind(this));
      this.$select.addEventListener('keydown', this.onSelectKeydown.bind(this));
    }
    this.$itemList.addEventListener('click', this.onItemClick.bind(this));
    this.$itemList.addEventListener('keydown', this.onItemKeydown.bind(this));
    window.addEventListener('hashchange', this.onHashchange.bind(this));
    this.$tabBar.addEventListener(
      'resendEvents',
      this.onResendEvents.bind(this)
    );

    // Select tab per URL parameter
    this.onHashchange();
  }

  static onResize(tabBars) {
    tabBars.forEach((tabBar) => {
      const $tabBar = getTarget(tabBar.target, '.tab-bar');
      const $items = $tabBar.querySelector('.tab-bar__list');
      const isGlossary = $tabBar.classList.contains('tab-bar--glossary');

      if (!isGlossary) {
        // Reset back
        $tabBar.classList.remove('tab-bar--menu');
        $tabBar.classList.add('tab-bar--list', 'tab-bar--uninitalized');

        // Get widths and collapse status
        const tabBarWidth = $tabBar
          .querySelector('.tab-bar__inner')
          .getBoundingClientRect().width;
        let itemsWidth = 1;
        $items.querySelectorAll('.tab-bar__list-item').forEach(($item) => {
          itemsWidth += $item.getBoundingClientRect().width + 20;
        });
        const isCollapsed = itemsWidth > tabBarWidth;

        // Switch between list and menu
        TabBar.switchBetweenListAndMenu($tabBar, isCollapsed);
      }
    });
  }

  static switchBetweenListAndMenu($tabBar, isCollapsed) {
    const $items = $tabBar.querySelector('.tab-bar__list');
    const isLinkList = $tabBar.classList.contains('tab-bar--links');

    // Switch class
    $tabBar.classList[isCollapsed ? 'add' : 'remove']('tab-bar--menu');
    $tabBar.classList[isCollapsed ? 'remove' : 'add']('tab-bar--list');

    // Remove uninitalized class
    $tabBar.classList.remove('tab-bar--uninitalized');

    // Switch role of list container
    if (isCollapsed) {
      $items.setAttribute('role', 'menu');
    } else if (!isLinkList) {
      $items.setAttribute('role', 'tablist');
    } else {
      $items.removeAttribute('role');
    }
    $items.hidden = isCollapsed;

    // Update list items
    $items.querySelectorAll('.tab-bar__item').forEach(($item) => {
      // Switch role
      if (isCollapsed) {
        $item.setAttribute('role', 'menuitemradio');
      } else if (!isLinkList) {
        $item.setAttribute('role', 'tab');
      } else {
        $item.removeAttribute('role');
      }

      // Update aria props for current active item
      if (isCollapsed && $item.getAttribute('aria-selected') === 'true') {
        $item.removeAttribute('aria-selected');
        $item.setAttribute('aria-checked', 'true');
      } else if (
        !isCollapsed &&
        $item.getAttribute('aria-checked') === 'true'
      ) {
        $item.removeAttribute('aria-checked');
        $item.setAttribute('aria-selected', 'true');
      }
    });
  }

  openMenu() {
    // Set state
    this.$select.setAttribute('aria-expanded', 'true');
    this.$itemList.hidden = false;

    // Get current active item
    const $checked = this.$itemList.querySelector('[aria-checked="true"]');
    $checked.focus();

    // Catch outside clicks
    document.addEventListener('click', this.onOutsideClickBinded);
  }

  closeMenu() {
    // Set state
    this.$select.setAttribute('aria-expanded', 'false');
    this.$itemList.hidden = true;
  }

  toggleMenu() {
    const expanded = this.$select.getAttribute('aria-expanded') === 'true';
    return expanded ? this.closeMenu() : this.openMenu();
  }

  focusNext($currentItem, $startItem) {
    // Determine which item is the startItem (first or last)
    const down = $startItem === this.$items[0];

    // Helper function for getting next legitimate element
    const move = ($el) => {
      const $nextThing = down
        ? $el.parentNode.nextElementSibling
        : $el.parentNode.previousElementSibling;

      return ($nextThing && $nextThing.firstElementChild) || $startItem;
    };

    // Move and focus
    const $nextItem = move($currentItem);
    $nextItem.focus();
  }

  openByHash() {
    if (window.location.hash) {
      const $target = document.getElementById(
        window.location.hash.substring(1)
      );

      if ($target && $target.matches('.tab-bar__item')) {
        $target.click();
        return true;
      }
    }

    return false;
  }

  onOutsideClick(event) {
    if (
      !this.$itemList.contains(event.target) &&
      !this.$select.contains(event.target)
    ) {
      document.removeEventListener('click', this.onOutsideClickBinded);
      this.closeMenu();
    }
  }

  onHashchange() {
    this.openByHash();
  }

  onResendEvents() {
    if (!this.openByHash()) {
      const $item = this.$itemList.querySelector(
        '.tab-bar__item[aria-selected="true"]'
      );
      if ($item) {
        $item.click();
      }
    }
  }

  onSelect() {
    this.toggleMenu();
  }

  onSelectKeydown(event) {
    // Also toggle on down arrow
    if (event.keyCode === 40) {
      if (this.$itemList.hidden) {
        this.openMenu();
      } else {
        this.$items[0].focus();
      }
    }

    // close menu on up arrow
    if (event.keyCode === 38) {
      this.closeMenu();
    }
  }

  onItemClick(event) {
    const $target = getTarget(event.target, '.tab-bar__item');
    const isMenuCurrently = this.$tabBar.classList.contains('tab-bar--menu');

    if ($target && $target.href) {
      // Remove old active item
      this.$items.forEach(($item) => {
        $item.classList.remove('tab-bar__item--active');

        if (isMenuCurrently) {
          $item.removeAttribute('aria-selected');
        } else {
          $item.setAttribute('aria-selected', 'false');
          $item.setAttribute('tabindex', '0');
        }

        $item.removeAttribute('aria-checked');
      });

      if (this.isTablist) {
        // If is tablist Send selectTab event to /javascripts/utils/TabBox.js
        const selectTabEvent = new CustomEvent('selectTab', {
          bubbles: true,
        });

        $target.dispatchEvent(selectTabEvent);
        event.preventDefault();
      }

      // Set new active
      $target.setAttribute(
        isMenuCurrently ? 'aria-checked' : 'aria-selected',
        'true'
      );

      if (!isMenuCurrently) {
        $target.setAttribute('tabindex', '-1');
      }

      $target.classList.add('tab-bar__item--active');

      // Update select text
      if (!this.isGlossary) {
        const $text = $target.querySelector('.tab-bar__text');
        const $currentSelect = this.$select.querySelector(
          '.tab-bar__select-current'
        );
        const $currentText = $currentSelect.querySelector(
          '.tab-bar__select-text'
        );
        $currentText.textContent = $text.textContent;

        // Update select icon
        const $icon = $target.querySelector('.tab-bar__icon');
        const $currentIcon = $currentSelect.querySelector('.tab-bar__icon');
        if ($currentIcon) {
          $currentIcon.parentNode.removeChild($currentIcon);
        }

        if ($icon) {
          $currentSelect.insertBefore(
            $icon.cloneNode(true),
            $currentSelect.firstChild
          );
        }
      }

      // Close menu
      if (isMenuCurrently) {
        this.closeMenu();
      }
    }
  }

  onItemKeydown(event) {
    const $target = getTarget(event.target, '.tab-bar__item');
    const isMenuCurrently = this.$tabBar.classList.contains('tab-bar--menu');

    if (!$target) {
      return;
    }

    if (isMenuCurrently) {
      // Down
      if (event.keyCode === 40) {
        event.preventDefault();
        this.focusNext($target, this.$items[0]);
      }

      // Up
      if (event.keyCode === 38) {
        event.preventDefault();
        this.focusNext($target, this.$items[this.$items.length - 1]);
      }

      // Esc / tab
      if (event.keyCode === 27 || event.keyCode === 9) {
        this.toggleMenu();
      }

      // If escape, refocus menu button
      if (event.keyCode === 27) {
        event.preventDefault();
        this.$select.focus();
      }

      if (event.keyCode === 32) {
        event.preventDefault();
        this.onItemClick(event);
      }
    } else {
      // Right
      if (event.keyCode === 39) {
        event.preventDefault();
        this.focusNext($target, this.$items[0]);
      }

      // Left
      if (event.keyCode === 37) {
        event.preventDefault();
        this.focusNext($target, this.$items[this.$items.length - 1]);
      }

      // Space-Bar
      if (event.keyCode === 32) {
        event.preventDefault();
        this.onItemClick(event);
      }
    }
  }
}

// Init
const observer = new ResizeObserver(TabBar.onResize);
document
  .querySelectorAll('.tab-bar')
  .forEach(($tabBar) => new TabBar($tabBar, observer));
